import { SupabaseClient } from '@supabase/supabase-js';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';

import type { RoastData } from 'features/roast/utils';
import { TanstackQueryName } from 'types/frontend';
import { Database } from 'types/supabase';

interface IGetRoastDetailQueryFnParams {
  supabase: SupabaseClient<Database>;
  roastId: string;
}

const getRoastDetailQueryFn = async ({
  supabase,
  roastId,
}: IGetRoastDetailQueryFnParams): Promise<RoastData> => {
  const { data, error } = await supabase
    .from('roast')
    .select('*')
    .eq('id', roastId)
    .single();

  if (error) throw error;

  return data as unknown as RoastData;
};

interface IGetRoastDetailQueryParams {
  roastId?: string | null;
}

interface IGetRoastDetailQueryResponse {
  data: RoastData | null;
  isPending: boolean;
}

export const useGetRoastDetailQuery = ({
  roastId,
}: IGetRoastDetailQueryParams): IGetRoastDetailQueryResponse => {
  const supabase = useSupabaseTypedClient();

  const { data, isPending } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetRoastDetail, roastId],
    queryFn: () =>
      getRoastDetailQueryFn({
        supabase,
        roastId: roastId!,
      }),
    enabled: Boolean(roastId),
    // Poll every 5s while the roast is still processing, stop once ready/rejected
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'processing' ? 5000 : false;
    },
  });

  return {
    data: data ?? null,
    isPending,
  };
};
